import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { FEMachine } from '@/types/flexe';
import { useToast } from '@/hooks/use-toast';

export function useFEMachines() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: machines = [], isLoading } = useQuery({
    queryKey: ['fe-machines'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('fe_machines')
        .select('*')
        .order('name');
      
      if (error) throw error;
      return data as FEMachine[];
    }
  });

  const createMachine = useMutation({
    mutationFn: async (machine: Omit<FEMachine, 'id' | 'created_at' | 'updated_at' | 'tenant_id'>) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('fe_machines')
        .insert({ ...machine, tenant_id: user.id })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fe-machines'] });
      toast({ title: 'Machine toegevoegd' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Fout bij toevoegen machine', 
        description: error.message,
        variant: 'destructive' 
      });
    }
  });

  const updateMachine = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<FEMachine> }) => {
      const { error } = await supabase
        .from('fe_machines')
        .update(updates)
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fe-machines'] });
      toast({ title: 'Machine bijgewerkt' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Fout bij bijwerken machine', 
        description: error.message,
        variant: 'destructive' 
      });
    }
  });

  const deleteMachine = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('fe_machines')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fe-machines'] });
      toast({ title: 'Machine verwijderd' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Fout bij verwijderen machine', 
        description: error.message,
        variant: 'destructive' 
      });
    }
  });

  return {
    machines,
    isLoading,
    createMachine: createMachine.mutate,
    updateMachine: updateMachine.mutate,
    deleteMachine: deleteMachine.mutate,
    isCreating: createMachine.isPending,
    isUpdating: updateMachine.isPending,
    isDeleting: deleteMachine.isPending
  };
}
